import { connect } from 'react-redux';
import coterie_black_logo from '../../icons/coterie_logo_black.svg';
import quickbooks_logo from '../../icons/quickbooks_logo.png';
import {
  selectContactFirstName,
  selectContactLastName,
  selectContactPhone,
  selectContactEmail,
  selectLocationState,
  selectPolicyTypes,
} from '../../selectors/application';
import { selectPartnerInfo } from '../../selectors';
import { getPartnerFromApplication } from '../../selectors/routing';
import Modal, { PrefilledAppDetailsBar } from '../../elements/Modal';
import styled from 'styled-components';
import { setPartnerIntroViewed } from '../../actions/application';
import { PREFILLED_INTRO_TEXT, QUICKBOOKS_PARTNER_IDS, PREFILLED_INTRO_NO_DATA_TEXT } from '../../constants';
import { getEnvironment } from '../../helpers/getEnvironment';
import formatPhone from '../../helpers/formatPhone';
import { DesktopAndTablet, Mobile } from '../../helpers/responsive';
import { selectReferralPartnerId } from '../../selectors/referralPartner';

const Field = styled.p`
  color: ${(props) => props.theme.pageComponents.shared.prefilledSummary.field.textColor};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 24px;
  margin: 0px 0px 4px 0px;
  word-wrap: break-word;
  font-family: ${(props) => props.theme.font.typeface.primary};
`;
const Value = styled.p`
  color: ${(props) => props.theme.pageComponents.shared.prefilledSummary.value.textColor};
  font-size: 16px;
  letter-spacing: 0;
  line-height: 24px;
  margin-top: 0;
  font-family: ${(props) => props.theme.font.typeface.secondary};
  overflow-wrap: break-word;
  word-wrap: break-word;
`;
const FieldContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 8px;
  @media (max-width: 767px) {
    grid-template-columns: none;
    gap: 4px;
  }
`;

const LogoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const QuickBooksLogo = styled.img`
  height: 30px;
  @media screen and (max-width: 768px) {
    height: 24px;
  }
`;
const CoterieLogo = styled.img`
  height: 30px;
  @media screen and (max-width: 768px) {
    height: 24px;
  }
`;
const Plus = styled.span`
  font-size: 25px;
  margin: 0 8px;
`;

const P = styled.p`
  color: ${(props) => props.theme.pageComponents.shared.prefilledSummary.p.textColor};
  font-family: ${(props) => props.theme.font.typeface.secondary};
  font-size: 15px;
  letter-spacing: 0;
  line-height: 24px;
  margin-top: 0px;
`;

const PartnerName = styled.span`
  font-weight: bold;
  font-size: 30px;
  @media screen and (max-width: 768px) {
    font-size: 20px;
    line-height: 26px;
  }
`;

const FinePrintText = styled.p`
  font-family: ${(props) => props.theme.font.typeface.secondary};
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.09px;
  color: #626266;
  text-align: center;
  margin: 0;
  padding: 0 24px 16px;

  @media screen and (max-width: 768px) {
    text-align: left;
  }
`;

const FinePrintLink = styled.a`
  font-family: ${(props) => props.theme.font.typeface.primary};
  font-weight: 600;
  color: ${(props) => props.theme.colors.primary.zero};
  text-decoration: none;
`;

const MobileDetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
`;

const MobileLogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  ${Plus} {
    font-size: 16px;
    margin: 0;
  }

  ${CoterieLogo} {
    height: 16px;
  }
`;

interface PrefilledSummaryProps {
  partnerInfo: {
    introViewed: boolean;
    id: string;
    name: string;
  };
  setPartnerIntroViewed: (val: boolean) => void;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  state?: string;
  policyType?: string[];
  referralPartnerId?: string;
}

const PrefilledSummary = ({
  partnerInfo,
  setPartnerIntroViewed,
  firstName,
  lastName,
  phone,
  email,
  state,
  policyType = [],
  referralPartnerId,
}: PrefilledSummaryProps) => {
  const { id, name, introViewed } = partnerInfo;
  const closeModal = () => {
    setPartnerIntroViewed(true);
  };

  const applicationValues = [
    { title: 'First Name', value: firstName },
    { title: 'Last Name', value: lastName },
    { title: 'Phone', value: phone && formatPhone(phone) },
    { title: 'State', value: state },
    { title: 'Policy Type', value: policyType.length > 0 && policyType.join('+') },
    { title: 'Email', value: email },
  ];

  const isIntuit =
    QUICKBOOKS_PARTNER_IDS[getEnvironment()] === id || QUICKBOOKS_PARTNER_IDS[getEnvironment()] === referralPartnerId;

  const anyApplicationValues = applicationValues
    .filter((values) => values.title !== 'State')
    .some((field) => field.value);
  const isOpen = Boolean(id && !introViewed && (isIntuit || anyApplicationValues));

  const RenderLogoHeader = () => (
    <LogoHeader>
      {isIntuit ? <QuickBooksLogo src={quickbooks_logo} alt="Quickbooks logo" /> : <PartnerName>{name}</PartnerName>}
      <Plus>+</Plus>
      <CoterieLogo src={coterie_black_logo} alt="Coterie Logo" />
    </LogoHeader>
  );

  const detailsBar = (
    <PrefilledAppDetailsBar data-cy="prefilled-summary-modal">
      <>
        <DesktopAndTablet>
          <RenderLogoHeader />
        </DesktopAndTablet>
        <Mobile>
          <MobileDetailContent>
            {isIntuit ? (
              <QuickBooksLogo src={quickbooks_logo} alt="Quickbooks logo" />
            ) : (
              <PartnerName>{name}</PartnerName>
            )}
            <MobileLogoRow>
              <Plus>+</Plus>
              <CoterieLogo src={coterie_black_logo} alt="Coterie Logo" />
            </MobileLogoRow>
          </MobileDetailContent>
        </Mobile>
      </>
    </PrefilledAppDetailsBar>
  );

  const finePrint = (
    <FinePrintText>
      By continuing, you agree to Coterie&apos;s{' '}
      <FinePrintLink href={`https://${import.meta.env.VITE_APP_DOMAIN}/privacy-policy`} target="_blank" rel="noopener">
        Privacy Policy
      </FinePrintLink>{' '}
      and{' '}
      <FinePrintLink
        href={`https://${import.meta.env.VITE_APP_DOMAIN}/terms-conditions`}
        target="_blank"
        rel="noopener"
      >
        Terms and Conditions
      </FinePrintLink>
      .
    </FinePrintText>
  );

  return (
    <Modal
      title="Welcome"
      isOpen={isOpen}
      prefilledAppDetailsBar={detailsBar}
      onRequestClose={closeModal}
      label="Prefilled application modal"
      buttonOnClick={closeModal}
      buttonCyLabel="prefilled-summary-submit-button"
      buttonText="Continue"
      footerContent={finePrint}
    >
      {anyApplicationValues ? (
        <>
          <P>{PREFILLED_INTRO_TEXT(name)}</P>
          <FieldContainer>
            {applicationValues.map(
              (item, i) =>
                item.value && (
                  <div key={i}>
                    <Field>{item.title}</Field>
                    <Value>{item.value}</Value>
                  </div>
                )
            )}
          </FieldContainer>
        </>
      ) : (
        <P>{PREFILLED_INTRO_NO_DATA_TEXT(name)}</P>
      )}
    </Modal>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  partnerInfo: getPartnerFromApplication(state),
  firstName: selectContactFirstName(state),
  lastName: selectContactLastName(state),
  phone: selectContactPhone(state),
  email: selectContactEmail(state),
  state: selectLocationState(state),
  policyType: selectPolicyTypes(state),
  partner: selectPartnerInfo(state),
  referralPartnerId: selectReferralPartnerId(state),
});

const mapDispatchToProps = {
  setPartnerIntroViewed,
};

export default connect(mapStateToProps, mapDispatchToProps)(PrefilledSummary);
